<template>
	<app-page>
		<app-heading :title="'Integrations logs'">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.integration"
							label="Integrations"
							:items="integrations"
							@change="fetchConnections"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.integration_connection"
							label="Connections"
							:items="connections"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-select
							v-model="filters.status"
							label="Status"
							:items="statuses"
							clearable
							dense
						></input-select>
				</app-cell>
				<app-cell cols="6" sm="3">
					<input-text
						v-model="filters.unit_code"
						label="Unit Code"
						clearable
						dense
					></input-text>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="logs"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:sort-by="'ts'"
					:sort-desc="true"
					@update="fetchLogs"
				>
					<template v-slot:status="{ item }">
						<span
							v-if="item.status === 'warning'"
							class="warning--text"
						>Warning</span>
						<span
							v-if="item.status === 'success'"
							class="success--text"
						>Success</span>
						<span
							v-if="item.status === 'error'"
							class="error--text"
						>Error</span>
					</template>
					<template v-slot:integration="{ item }">
						<div>{{ getIntegrationName(item.integration) }}</div>
					</template>
					<template v-slot:unit_code="{ item }">
						<span v-if="item.unit_code">{{ item.unit_code }}</span>
						<i v-else>None</i>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							table
							@click="onLogDetails(item.integration, item._id)"
						>
							Details
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>

		<app-dialog
			v-model="log.show"
			max-width="800"
			height="600"
			max-height="600"
			title="Log details"
		>
			<app-dialog-body>
				<app-row>
					<app-cell xs6>
						<div><b>Request</b></div>
						<app-json :data="log.request" open />
					</app-cell>
					<app-cell xs1></app-cell>
					<app-cell xs5>
						<div><b>Details</b></div>
						<app-json :data="log.details" open />
					</app-cell>
				</app-row>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat="flat"
					@click="log.show = false"
				>Close</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import filters from '@/mixins/filters'
import { mapSelectOptions, dynamicNewMapEntries } from '@/utils/maps'
import { integrations as integrationTabs } from '@/datasources/tabs'

export default {
	mixins: [filters],
	async created() {
		this.tabs = integrationTabs(this.integrationId)
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get())

		if (this.filters.integration) {
			this.fetchConnections()
		}
	},
	data() {
		return {
			tabs: [],
			logs: [],
			log: {
				show: false,
				request: {},
				details: [],
			},
			integrationsMap: {},
			connections: [],
			integrations: [],
			count: -1,
			refresh: false,
			statuses: [{
				text: 'Success',
				value: 'success',
			}, {
				text: 'Warning',
				value: 'warning',
			}, {
				text: 'Error',
				value: 'error',
			}],
			headers: [{
				text: 'Timestamp',
				value: 'ts',
				sortable: true,
				format: 'datetime',
			}, {
				text: 'status',
				value: 'status',
				sortable: true,
			}, {
				text: 'Integration',
				value: 'integration',
				sortable: true,
			}, {
				text: 'Unit',
				value: 'unit_code',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			filters: {
				integration: '',
				integration_connection: '',
				unit_code: '',
				status: '',
			},
		}
	},
	methods: {
		getIntegrationName(id) {
			return this.integrationsMap[id] && this.integrationsMap[id].name
				? this.integrationsMap[id].name
				: 'UNDEFINED'
		},

		async updateIntegrationsMap() {
			const res = await dynamicNewMapEntries(
				this.logs,
				'integration',
				this.integrationsMap,
				this.$ravello.request()
					.v(2)
					.to('integrations')
					.field('name'),
			)

			if (res.err) {
				this.$notifications.pushAlert('error', res.err.data ? res.err.data.message : res.err.message)
			}

			this.integrationsMap = {
				...this.integrationsMap,
				...res.entries,
			}
		},

		async fetchConnections() {
			if (this.filters.integration) {
				this.connections = mapSelectOptions(await this.$ravello.request()
					.v(2)
					.to('integrations')
					.to(this.filters.integration)
					.to('connections')
					.field(['name'])
					.limit(100)
					.get())
			} else {
				this.connections = []
				this.filters.integration_connection = null
			}
		},

		async fetchLogs(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('integrationslogs')
				.fromPagination(pagination)
				.fields([
					'ts',
					'status',
					'integration',
					'unit_code',
				])

			if (this.appliedFilters.integration) {
				request.param('integration', this.appliedFilters.integration)
			}
			if (this.appliedFilters.integration_connection) {
				request.param('integration_connection', this.appliedFilters.integration_connection)
			}
			if (this.appliedFilters.unit_code) {
				request.param('unit_code', this.appliedFilters.unit_code)
			}
			if (this.appliedFilters.status) {
				request.param('status', this.appliedFilters.status)
			}

			try {
				this.logs = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}

				await this.updateIntegrationsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async onLogDetails(integrationId, id) {
			this.$store.dispatch('app/startLoading')
			const request = this.$ravello.request()
				.v(2)
				.to('integrationslogs')
				.to(id)

			try {
				const log = await request.get()
				this.log.request = log.request
				this.log.details = log.details
				this.log.show = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
